/**
 * EdgeFlow — directional cones drifting along every move edge.
 *
 * The traveling-glow comet in Edges.tsx conveys direction by brightening
 * vertices along the line, which reads well up close but washes out at
 * distance (a 1px line can only get so bright). These are real geometry:
 * small cones that ride the same quadratic bezier as the edge, pointed
 * down the tangent, so "which way does this move go" is legible at any
 * zoom and from any angle.
 *
 * Implementation notes:
 *  - ONE InstancedMesh for every cone in the scene (≈ moves × CONES_PER_EDGE,
 *    a few hundred instances) — one draw call, no per-edge objects.
 *  - Rides the SAME control point Edges.tsx tessellates, via the shared
 *    subArcControl() — so cones stay glued to the drawn line through
 *    fanned sleeves and the two-lane split of bidirectional pairs. A
 *    collapsed sleeve shows cones on its single arc; an expanded one
 *    gets a set per sub-arc.
 *  - Gated on the same `showEdgeDirection` toggle as the comet, and it
 *    mirrors Edges.tsx's visibility rule so cones only ride lines that
 *    are actually drawn: with `edges: off` the map still shows the focal
 *    node's edges, so cones follow that focal set rather than vanishing.
 *  - Instances that aren't in use are scaled to zero rather than removed,
 *    so the buffer never resizes mid-flight.
 */
import { useFrame } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useAppStore } from '../state/store';
import type { Graph } from '../types/domain';
import type { EdgeDir } from './Scene';
import { EDGE_BRIGHT } from '../zones/palette';
import { subArcControl } from './bezier';
import { nodeRadius } from './sizing';
import type { ForceLayout } from './useForceLayout';

/** Mirror of Edges.tsx — the hard cap on sub-arcs a sleeve can fan into. */
const MAX_SUB_ARCS = 12;
/** Cones per sub-arc. 2 reads as "flow" without crowding short edges. */
const CONES_PER_EDGE = 2;
/** Laps per second. Half the comet's 0.7 — at full speed the cones read
 *  as darting rather than drifting. */
const TRAVEL_SPEED = 0.35;
/** Cone dimensions in world units (node radii run ~0.65–1.9). */
const CONE_LEN = 0.62;
const CONE_RADIUS = 0.17;
/** Keep cones off the node spheres at both ends of the arc. */
const T_MIN = 0.16;
const T_MAX = 0.84;

const UP = new THREE.Vector3(0, 1, 0);

export function EdgeFlow({
  graph,
  layout,
  glowEdges,
  forceEdges = false,
}: {
  graph: Graph;
  layout: ForceLayout;
  /** Same focal set Edges.tsx paints bright. */
  glowEdges: Map<number, EdgeDir>;
  /** Gameplan filter forces every edge visible. */
  forceEdges?: boolean;
}) {
  // Must match what Nodes.tsx draws, or edges end short of the sphere
  // or plunge through it — see the note at the top of sizing.ts.
  const sizeBy = useAppStore((s) => s.sizeBy);
  const showEdgeDirection = useAppStore((s) => s.showEdgeDirection);
  const showEdges = useAppStore((s) => s.showEdges);
  // Sleeve expansion drives WHICH sub-arcs exist, so the cones have to
  // track it or they ride a spine that's no longer drawn.
  const sleevesExpanded = useAppStore((s) => s.sleevesExpanded);
  const hoveredSleeveIndex = useAppStore((s) => s.hoveredSleeveIndex);
  const meshRef = useRef<THREE.InstancedMesh>(null!);
  const phaseRef = useRef(0);

  // Indices of the move edges we decorate (counters ride their parent
  // move's arc, so they'd double up — skip them).
  const moveEdges = useMemo(
    () =>
      graph.edges
        .map((e, i) => ({ e, i }))
        .filter(({ e }) => e.kind === 'move'),
    [graph],
  );
  // One cone-set per SUB-ARC. An expanded sleeve shows every one of its
  // moves as its own arc, so budget by total move count (capped per
  // sleeve) rather than by sleeve count.
  const capacity = Math.max(
    1,
    moveEdges.reduce(
      (acc, { e }) =>
        acc + Math.min(e.moveIds?.length ?? 1, MAX_SUB_ARCS) * CONES_PER_EDGE,
      0,
    ),
  );

  // Endpoint radii per edge — cached so the per-frame loop doesn't hit
  // the graph maps. Used to bias the t-range so cones clear big hubs.
  const radii = useMemo(
    () =>
      moveEdges.map(({ e }) => {
        const s = graph.byId.get(e.source);
        const t = graph.byId.get(e.target);
        return {
          src: s ? nodeRadius(s, sizeBy) : 0.8,
          tgt: t ? nodeRadius(t, sizeBy) : 0.8,
        };
      }),
    [moveEdges, graph],
  );

  const dummy = useMemo(() => new THREE.Object3D(), []);
  const tangent = useMemo(() => new THREE.Vector3(), []);
  const quat = useMemo(() => new THREE.Quaternion(), []);

  // Reset instance colours once — a single flat tint keeps the cones
  // reading as "flow markers" rather than competing with node colour.
  useEffect(() => {
    const mesh = meshRef.current;
    if (!mesh) return;
    mesh.frustumCulled = false;
  }, []);

  // Mirror Edges.tsx's visibility rule exactly, or cones end up floating
  // on edges that aren't drawn (or missing on edges that are):
  //   globalHide = !showEdges && no focal glow && not force-shown
  // And when there IS a focal set, only those edges are prominent — so
  // only they get cones, instead of littering the dimmed background.
  const hasActive = glowEdges.size > 0;
  const globalHide = !showEdges && !hasActive && !forceEdges;
  const active = showEdgeDirection && !globalHide;

  useFrame((_, dt) => {
    const mesh = meshRef.current;
    const pos = layout.posRef.current;
    if (!mesh || !pos) return;

    if (!active) {
      // Collapse everything; cheap and keeps the buffer stable.
      if (mesh.count !== 0) {
        mesh.count = 0;
        mesh.instanceMatrix.needsUpdate = true;
      }
      return;
    }
    mesh.count = capacity;

    phaseRef.current =
      (phaseRef.current + Math.min(0.25, dt) * TRAVEL_SPEED) % 1;
    const base = phaseRef.current;

    const allExpanded = sleevesExpanded === 'expanded';
    let n = 0;
    for (let k = 0; k < moveEdges.length; k++) {
      const { e, i: edgeIdx } = moveEdges[k];
      const moveCount = Math.min(e.moveIds?.length ?? 1, MAX_SUB_ARCS);
      const isSleeve = moveCount > 1;
      // Same expansion rule Edges.tsx uses: the global toggle, or this
      // specific sleeve being hovered.
      const expanded =
        isSleeve && (allExpanded || hoveredSleeveIndex === edgeIdx);
      // Collapsed sleeves draw only sub-arc 0, so only it gets cones.
      const arcsShown = expanded ? moveCount : 1;

      const shown = hasActive
        ? glowEdges.has(edgeIdx)
        : showEdges || forceEdges;
      const si = layout.nodeIndex.get(e.source);
      const ti = layout.nodeIndex.get(e.target);
      const ok = shown && si !== undefined && ti !== undefined;

      const ax = ok ? pos[si! * 3] : 0;
      const ay = ok ? pos[si! * 3 + 1] : 0;
      const az = ok ? pos[si! * 3 + 2] : 0;
      const bx = ok ? pos[ti! * 3] : 0;
      const by = ok ? pos[ti! * 3 + 1] : 0;
      const bz = ok ? pos[ti! * 3 + 2] : 0;

      const chord = Math.hypot(bx - ax, by - ay, bz - az) || 1;
      // Clip so cones emerge from the source sphere's surface and vanish
      // into the target's. Chord-relative so long edges aren't over-clipped.
      const tLo = Math.max(T_MIN, radii[k].src / chord);
      const tHi = Math.min(T_MAX, 1 - radii[k].tgt / chord);
      const span = Math.max(0.05, tHi - tLo);

      for (let arcIdx = 0; arcIdx < moveCount; arcIdx++) {
        const arcVisible = ok && arcIdx < arcsShown;
        // Control point for THIS sub-arc — identical call to the one
        // Edges.tsx tessellates, so cones sit exactly on the drawn line
        // (fanned sleeves and bidirectional lanes included).
        const c3 = arcVisible
          ? subArcControl(
              ax, ay, az,
              bx, by, bz,
              arcIdx,
              moveCount,
              expanded,
              e.dirSign ?? 0,
            )
          : null;

        for (let c = 0; c < CONES_PER_EDGE; c++) {
          if (!c3) {
            dummy.position.set(0, 0, 0);
            dummy.scale.setScalar(0);
            dummy.updateMatrix();
            mesh.setMatrixAt(n++, dummy.matrix);
            continue;
          }
          const { cx, cy, cz } = c3;
          // Evenly spaced phases so each arc shows a steady procession.
          const u = (base + c / CONES_PER_EDGE) % 1;
          const t = tLo + u * span;
          const it = 1 - t;

          const px = it * it * ax + 2 * it * t * cx + t * t * bx;
          const py = it * it * ay + 2 * it * t * cy + t * t * by;
          const pz = it * it * az + 2 * it * t * cz + t * t * bz;

          // Tangent = B'(t) = 2(1-t)(C-A) + 2t(B-C) — the direction of
          // travel, which is exactly what the cone should point down.
          tangent.set(
            2 * it * (cx - ax) + 2 * t * (bx - cx),
            2 * it * (cy - ay) + 2 * t * (by - cy),
            2 * it * (cz - az) + 2 * t * (bz - cz),
          );
          if (tangent.lengthSq() < 1e-8) tangent.set(0, 1, 0);
          tangent.normalize();

          dummy.position.set(px, py, pz);
          quat.setFromUnitVectors(UP, tangent);
          dummy.quaternion.copy(quat);
          // Scale-fade at the clip boundaries so cones don't pop.
          const edgeFade = Math.min(1, Math.min(u, 1 - u) * 6);
          dummy.scale.setScalar(0.35 + 0.65 * edgeFade);
          dummy.updateMatrix();
          mesh.setMatrixAt(n++, dummy.matrix);
        }
      }
    }
    mesh.instanceMatrix.needsUpdate = true;
  });

  return (
    <instancedMesh
      ref={meshRef}
      args={[undefined, undefined, capacity]}
      // Cones are decorative — never block a node/edge raycast.
      raycast={() => null}
    >
      <coneGeometry args={[CONE_RADIUS, CONE_LEN, 6]} />
      <meshBasicMaterial
        color={EDGE_BRIGHT}
        transparent
        opacity={0.75}
        depthWrite={false}
        toneMapped={false}
      />
    </instancedMesh>
  );
}
